import React, { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { AISuggestionButton } from './AISuggestionButton'
import { TrashIcon, AIIcon, InfoIcon } from '@/components/icons'

const CharacterCounter = ({ current, max, recommendationLimit, className }) => {
    const remaining = max - current
    const isNearLimit = remaining <= 20
    const isOverLimit = remaining < 0
    const isOverRecommendation = recommendationLimit && current > recommendationLimit

    return (
        <div
            className={cn(
                'tw:text-xs tw:select-none tw:pointer-events-none tw:mr-1',
                isOverLimit
                    ? 'tw:text-red-500'
                    : isOverRecommendation
                      ? 'tw:text-yellow-500'
                      : isNearLimit
                        ? 'tw:text-yellow-500'
                        : 'tw:text-gray-400',
                className,
            )}
        >
            {current}/{max}
        </div>
    )
}

const TextVariation = ({
    value,
    onChange,
    onRemove,
    placeholder,
    maxLength,
    recommendationLimit,
    canRemove = true,
    disabled = false,
    isTextarea = false,
    aiSuggestions = [],
    onAISuggestionSelect,
    aiLoading = false,
    showAIOnFocus = false,
}) => {
    const [showAIDropdown, setShowAIDropdown] = React.useState(false)
    const dropdownRef = React.useRef(null)

    const handleAISuggestionClick = (suggestion) => {
        onAISuggestionSelect(suggestion)
        setShowAIDropdown(false)
    }

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowAIDropdown(false)
            }
        }

        if (showAIDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [showAIDropdown])

    return (
        <div className="tw:relative tw:mb-4" ref={dropdownRef}>
            <div className="tw:relative">
                {isTextarea ? (
                    <Textarea
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        maxLength={255}
                        disabled={disabled}
                        className={cn(
                            value.length > maxLength && 'tw:border-red-500',
                            recommendationLimit &&
                                value.length > recommendationLimit &&
                                value.length <= maxLength &&
                                'tw:border-yellow-500',
                        )}
                        onFocus={() => {
                            if (showAIOnFocus && aiSuggestions.length > 0) {
                                setShowAIDropdown(true)
                            }
                        }}
                    />
                ) : (
                    <Input
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        maxLength={255}
                        disabled={disabled}
                        className={cn(
                            'tw:h-[40px] tw:pr-32',
                            value.length > maxLength && 'tw:border-red-500',
                            recommendationLimit &&
                                value.length > recommendationLimit &&
                                value.length <= maxLength &&
                                'tw:border-yellow-500',
                        )}
                        onFocus={() => {
                            if (showAIOnFocus && aiSuggestions.length > 0) {
                                setShowAIDropdown(true)
                            }
                        }}
                    />
                )}

                {/* Character counter and action buttons */}
                <div
                    className={cn(
                        'tw:absolute tw:right-2 tw:flex tw:items-center tw:z-[10]',
                        isTextarea ? 'tw:top-2' : 'tw:top-1/2 tw:-translate-y-1/2',
                    )}
                >
                    {maxLength && (
                        <CharacterCounter
                            current={value.length}
                            max={maxLength}
                            recommendationLimit={recommendationLimit}
                        />
                    )}
                    {aiSuggestions.length > 0 && (
                        <AISuggestionButton
                            onClick={() => setShowAIDropdown(!showAIDropdown)}
                            suggestions={aiSuggestions}
                            loading={aiLoading}
                            buttonText=""
                            customIcon={<AIIcon size={16} />}
                        />
                    )}
                    {canRemove && (
                        <button
                            type="button"
                            onClick={onRemove}
                            className="tw:p-1 tw:transition-colors tw:flex tw:items-center tw:justify-center"
                            disabled={disabled}
                            title="Remove this variation"
                        >
                            <TrashIcon size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* AI Suggestions Dropdown - positioned relative to the whole TextVariation */}
            {showAIDropdown && aiSuggestions.length > 0 && (
                <div
                    className="tw:absolute tw:w-full tw:bg-[#09121d] tw:border tw:border-[#3B5168] tw:rounded-[2px] tw:z-[50] tw:p-2"
                    style={{
                        top: isTextarea ? '66px' : '48px',
                        left: '0',
                        right: '0',
                        boxShadow: '0 4px 24px rgba(5, 17, 29, 0.25)',
                    }}
                >
                    <div className="tw:pl-2 tw:text-sm tw:text-[#899CBD] tw:mb-2">Suggested by AI</div>
                    <div
                        className="tw:max-h-80 tw:overflow-y-auto tw:scrollbar-hide"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {aiSuggestions.map((suggestion, index) => (
                            <div
                                key={index}
                                className="tw:py-2 tw:px-2 tw:text-sm tw:text-white tw:leading-relaxed tw:cursor-pointer tw:hover:bg-[#17273C] tw:rounded tw:transition-colors"
                                onClick={() => handleAISuggestionClick(suggestion)}
                            >
                                {suggestion}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

const AddTextOptionButton = ({ onAddVariation, maxReached, disabled }) => {
    return (
        <Button
            variant="secondary"
            onClick={() => onAddVariation('')}
            disabled={disabled || maxReached}
            className="tw:text-[14px] tw:font-medium tw:flex tw:items-center tw:gap-1.5"
        >
            <Plus size={18} />
            <span>Add option</span>
        </Button>
    )
}

const ensureArray = (val) => {
    if (Array.isArray(val)) return val
    if (val == null || val === '') return ['']
    return [String(val)]
}

export const MultiCopyInput = ({
    label,
    variations = [''],
    onChange,
    maxVariations = 5,
    characterLimit,
    recommendationLimit,
    placeholder = '',
    aiSuggestions = [],
    aiLoading = false,
    tooltip = null,
    disabled = false,
    isTextarea = false,
    showAIOnFocus = false,
}) => {
    const [localVariations, setLocalVariations] = useState(() => ensureArray(variations))

    useEffect(() => {
        setLocalVariations(ensureArray(variations))
    }, [variations])

    const handleVariationChange = (index, value) => {
        const newVariations = [...localVariations]
        newVariations[index] = value
        setLocalVariations(newVariations)
        onChange(newVariations)
    }

    const handleAddVariation = (text = '') => {
        if (localVariations.length < maxVariations) {
            const newVariations = [...localVariations, text]
            setLocalVariations(newVariations)
            onChange(newVariations)
        }
    }

    const handleRemoveVariation = (index) => {
        if (localVariations.length > 1) {
            const newVariations = localVariations.filter((_, i) => i !== index)
            setLocalVariations(newVariations)
            onChange(newVariations)
        }
    }

    const handleAISuggestionSelect = (index, suggestion) => {
        const newVariations = [...localVariations]
        newVariations[index] = suggestion
        setLocalVariations(newVariations)
        onChange(newVariations)
    }

    const maxReached = localVariations.length >= maxVariations

    return (
        <div className="tw:mb-6">
            <div className="tw:flex tw:items-center tw:gap-2 tw:mb-3">
                <label className="tw:!text-sm tw:!font-bold tw:text-white">
                    {label} {localVariations.length > 1 && `(${localVariations.length} of ${maxVariations})`}
                </label>
                {tooltip ? (
                    <div className="tw:group tw:relative">
                        <InfoIcon size={20} className="tw:cursor-help" />
                        <div className="tw:absolute tw:bottom-full tw:left-0 tw:mb-2 tw:p-2 tw:bg-black tw:text-white tw:text-xs tw:rounded tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity tw:pointer-events-none tw:z-20 tw:w-64">
                            {tooltip}
                        </div>
                    </div>
                ) : (
                    <InfoIcon size={20} className="tw:cursor-help" />
                )}
            </div>

            {localVariations.map((variation, index) => (
                <TextVariation
                    key={index}
                    value={variation}
                    onChange={(value) => handleVariationChange(index, value)}
                    onRemove={() => handleRemoveVariation(index)}
                    placeholder={placeholder}
                    maxLength={characterLimit}
                    recommendationLimit={recommendationLimit}
                    canRemove={localVariations.length > 1}
                    disabled={disabled}
                    isTextarea={isTextarea}
                    aiSuggestions={aiSuggestions}
                    onAISuggestionSelect={(suggestion) => handleAISuggestionSelect(index, suggestion)}
                    aiLoading={aiLoading}
                    showAIOnFocus={showAIOnFocus}
                />
            ))}

            {!maxReached && (
                <AddTextOptionButton onAddVariation={handleAddVariation} maxReached={maxReached} disabled={disabled} />
            )}
        </div>
    )
}
